import { FC, ReactNode, useEffect } from 'react';
import { Redirect } from 'react-router';
import { IonSkeletonText } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { useAsyncEffect } from '../../support/useAsyncEffect';
import { InlineLoadError } from '../../components/InlineLoadError';
import { InnerhivePage } from '../../InnerhivePage';
import { ActiveCareMapProviderV2 } from '../../ActiveCareMapProvider';
import { CareMapState } from '../../caremap/CareMapState';
import { CaremapInsightsModelFactory } from '../../insights/CaremapInsightsModelFactory';
import { InsightsModel } from '../../insights/InsightsModel';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { CareTabsProvider } from '../CareTabsProvider';
import { CareRecipientSwitcher } from '../../navigation/CareRecipientSwitcher';
import {
    describeNoScoreReason,
    DOMAIN_LABEL,
    FULL_SCORE,
    IClientWellbeing,
    IDomainNote,
    IDomainReading,
    IWeeklyStanding,
    WELLBEING_EXPLAINER,
    monthDay
} from '@innerhive-internal/innerhive-common';
import { BRAND, CHART_INK, DOMAIN_STATE_COLOR, STATUS } from '../../organizations/insights/agencyChartTheme';
import { ClientWellbeingHex } from '../../organizations/insights/ClientWellbeingHex';
import { InsightsCard } from '../../organizations/insights/InsightsCard';
import { InsightsInfoTip } from '../../organizations/insights/InsightsInfoTip';
import { BeaSays } from '../../organizations/insights/BeaSays';
import { NameHelper } from '../../contact-methods/NameHelper';
import {
    buildClientStatus,
    describeDirection,
    describeMonthOnMonth,
    describeStatusNow,
    describeWeekOnWeek,
    IClientStatus,
    toClientFindings
} from './clientStatus';

const useStyles = createUseStyles({
    page: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 18,
        padding: '4px 0 24px'
    },

    summary: {
        marginTop: 10,
        fontSize: 13.5,
        lineHeight: 1.5,
        textAlign: 'center' as const,
        color: CHART_INK.strongLabel
    },

    facet: {
        padding: '12px 0',
        borderTop: `1px solid ${BRAND.tealEdge}`,

        '&:first-child': {
            borderTop: 'none'
        }
    },

    facetHead: {
        display: 'flex',
        alignItems: 'center',
        gap: 10
    },

    facetName: {
        flex: 1,
        fontSize: 13.5,
        fontWeight: 500,
        color: CHART_INK.strongLabel
    },

    facetScore: {
        fontSize: 12.5,
        color: CHART_INK.label,
        fontVariantNumeric: 'tabular-nums'
    },

    bar: {
        position: 'relative',
        height: 8,
        marginTop: 7,
        borderRadius: 4,
        background: 'var(--ion-color-step-100)',
        overflow: 'hidden'
    },

    barFill: {
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        borderRadius: 4
    },

    notes: {
        margin: '10px 0 0',
        padding: 0,
        listStyle: 'none',
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 8
    },

    note: {
        display: 'flex',
        gap: 9,
        fontSize: 12.5,
        lineHeight: 1.5,
        color: CHART_INK.label
    },

    noteMark: {
        flexShrink: 0,
        width: 7,
        height: 7,
        marginTop: 6,
        borderRadius: '50%'
    },

    noteTitle: {
        fontWeight: 500,
        color: CHART_INK.strongLabel
    },

    quiet: {
        marginTop: 8,
        fontSize: 12.5,
        color: CHART_INK.label
    },

    scores: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: 10,
        height: 130,
        paddingTop: 4
    },

    scoreColumn: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'center',
        gap: 6,
        height: '100%',
        justifyContent: 'flex-end'
    },

    scoreValue: {
        fontSize: 12.5,
        fontWeight: 600,
        color: CHART_INK.strongLabel,
        fontVariantNumeric: 'tabular-nums'
    },

    scoreBar: {
        width: '100%',
        maxWidth: 46,
        borderRadius: 5,
        minHeight: 4
    },

    scoreWeek: {
        fontSize: 11.5,
        color: CHART_INK.label
    },

    compare: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 14
    },

    compareCell: {
        padding: '13px 15px',
        borderRadius: 11,
        border: `1px solid ${BRAND.tealEdge}`,
        background: BRAND.tealWash
    },

    compareLabel: {
        fontSize: 11.5,
        fontWeight: 600,
        letterSpacing: '0.03em',
        textTransform: 'uppercase' as const,
        color: CHART_INK.label
    },

    compareLine: {
        marginTop: 5,
        fontSize: 13,
        lineHeight: 1.5,
        color: CHART_INK.strongLabel
    },

    notice: {
        marginBottom: 12,
        fontSize: 13.5,
        lineHeight: 1.6,
        color: CHART_INK.label
    },

    loading: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 14
    }
});

const when = (isoDate: string): string => {
    const day = new Date(isoDate);
    if (isNaN(day.getTime())) {
        return '';
    }
    return day.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

/**
 * Four weeks of scores, each week read on its own.
 *
 * Not interactive: a column that looks pressable and does nothing is worse than a plain mark, and
 * there is nothing behind a week here that the facets below do not already show in full.
 */
const WeeklyScores: FC<{ weeks: IWeeklyStanding[] }> = ({ weeks }) => {
    const classes = useStyles();

    if (weeks.length === 0) {
        return null;
    }

    return (
        <div className={classes.scores} data-id="status-weekly-scores">
            {weeks.map(week => (
                <div key={week.weekStartsOn} className={classes.scoreColumn} data-week={week.weekStartsOn}>
                    <span className={classes.scoreValue}>{week.score}</span>
                    <span
                        className={classes.scoreBar}
                        style={{
                            height: `${Math.max(4, (week.score / FULL_SCORE) * 100)}%`,
                            background: DOMAIN_STATE_COLOR[week.state]
                        }}
                    />
                    <span className={classes.scoreWeek}>{monthDay(week.weekStartsOn)}</span>
                </div>
            ))}
        </div>
    );
};

const NoteList: FC<{ notes: IDomainNote[] }> = ({ notes }) => {
    const classes = useStyles();

    return (
        <ul className={classes.notes}>
            {notes.map((note, index) => (
                <li key={`${note.observedOn}-${index}`} className={`${classes.note} mp-mask`}>
                    <span
                        className={classes.noteMark}
                        style={{ background: note.isIncident ? STATUS.incident : STATUS.worthWatching }}
                    />
                    <span>
                        <span className={classes.noteTitle}>{note.title}</span>
                        {note.narrative ? ` ${note.narrative}` : ''}
                        <span> ({when(note.observedOn)})</span>
                    </span>
                </li>
            ))}
        </ul>
    );
};

/** Incidents recorded against no part of daily life, which the diagram has no wedge for. */
const UnplacedIncidents: FC<{ notes: IDomainNote[] }> = ({ notes }) => {
    const classes = useStyles();

    if (notes.length === 0) {
        return null;
    }

    return (
        <div className={classes.facet} data-id="status-unplaced-incidents">
            <div className={classes.facetHead}>
                <span className={classes.facetName}>Not Tied to One Part of Life</span>
            </div>

            <NoteList notes={notes} />
        </div>
    );
};

/** One part of daily life, its score when there is one, and the notes behind it. */
const Facet: FC<{
    reading: IDomainReading;
    /** False for a client who cannot be compared with their own baseline, whose notes are shown without a score. */
    isScored: boolean;
}> = ({ reading, isScored }) => {
    const classes = useStyles();

    return (
        <div className={classes.facet} data-id="status-facet" data-domain={reading.domain}>
            <div className={classes.facetHead}>
                <span className={classes.facetName}>{DOMAIN_LABEL[reading.domain]}</span>
                {isScored && <span className={classes.facetScore}>{reading.score} / {FULL_SCORE}</span>}
            </div>

            {isScored && (
                <div className={classes.bar}>
                    <div
                        className={classes.barFill}
                        style={{ width: `${reading.score}%`, background: DOMAIN_STATE_COLOR[reading.state] }}
                    />
                </div>
            )}

            {reading.notes.length === 0
                ? <div className={classes.quiet}>Nothing raised about this recently.</div>
                : <NoteList notes={reading.notes} />}
        </div>
    );
};

/** This week against last, and this month against the one before, by how much was written down. */
const TrendsOverTimeCard: FC<{ status: IClientStatus }> = ({ status }) => {
    const classes = useStyles();

    return (
        <InsightsCard heading="Trends over Time">
            <div className={classes.compare}>
                <div className={classes.compareCell} data-id="status-week-on-week">
                    <div className={classes.compareLabel}>Week Over Week</div>
                    <div className={classes.compareLine}>{describeWeekOnWeek(status)}</div>
                </div>

                <div className={classes.compareCell} data-id="status-month-on-month">
                    <div className={classes.compareLabel}>Month Over Month</div>
                    <div className={classes.compareLine}>{describeMonthOnMonth(status)}</div>
                </div>
            </div>

            <BeaSays panel="status-compare">
                I count what has been written down, not how anybody feels. More observations can
                mean more people are noticing rather than more going wrong, and a quiet stretch can
                mean a settled one or simply that nobody has had a chance to write anything.
            </BeaSays>
        </InsightsCard>
    );
};

/** Four weeks of scores, for a client with a score to give. */
const OverTimeCard: FC<{ weeks: IWeeklyStanding[] }> = ({ weeks }) => (
    <InsightsCard heading="Over Time" blurb="Their score across the last four weeks, each week read on its own.">
        <WeeklyScores weeks={weeks} />

        <BeaSays panel="status-over-time">
            Each bar is that week on its own rather than everything piling up, which is the only way a
            recovery can show: read cumulatively a score can only ever fall.
        </BeaSays>
    </InsightsCard>
);

/** Every part of daily life with the notes behind it. */
const FacetsCard: FC<{ wellbeing: IClientWellbeing; isScored: boolean }> = ({ wellbeing, isScored }) => {
    // Hardest first when there is a score to order by, otherwise the diagram's clockwise order. The
    // diagram is for recognising a shape; this list is what somebody reads next, and a worklist
    // starts at the top.
    const facetOrder = isScored
        ? [...wellbeing.readings].sort((a, b) => a.score - b.score)
        : wellbeing.readings;

    return (
        <InsightsCard
            heading={isScored ? undefined : 'Recent Callouts'}
            blurb={isScored ? undefined : 'Key highlights across each Innerhive facet.'}
        >
            <UnplacedIncidents notes={wellbeing.unplacedIncidents} />

            {facetOrder.map(reading => (
                <Facet key={reading.domain} reading={reading} isScored={isScored} />
            ))}
        </InsightsCard>
    );
};

/**
 * The panels, shown once there are findings to read. Without a score the direction and the weeks
 * are left out rather than each saying there is none.
 */
const StatusCards: FC<{ status: IClientStatus; firstName?: string }> = ({ status, firstName }) => {
    const classes = useStyles();
    const isScored = status.wellbeing.overall !== 'unscored';

    return (
        <>
            <InsightsCard
                heading={firstName
                    ? <>How's <span className="mp-mask">{firstName}</span> doing?</>
                    : 'How are they doing?'}
                blurb={isScored ? 'Scored against what is usually recorded for this person.' : undefined}
                action={(
                    <InsightsInfoTip label="About this reading" summary={describeNoScoreReason(status.wellbeing)}>
                        {WELLBEING_EXPLAINER}
                    </InsightsInfoTip>
                )}
            >
                <ClientWellbeingHex wellbeing={status.wellbeing} reading={status.reading} emphasis />

                <div className={`${classes.summary} mp-mask`} data-id="status-summary">
                    {describeStatusNow(status)}
                </div>

                {isScored && <BeaSays panel="status-now">{describeDirection(status)}</BeaSays>}
            </InsightsCard>

            <TrendsOverTimeCard status={status} />

            {isScored && <OverTimeCard weeks={status.weeks} />}

            <FacetsCard wellbeing={status.wellbeing} isScored={isScored} />
        </>
    );
};

/** Said plainly when there is no score to show, rather than showing a score nobody has earned. */
const StatusNotice: FC<{ title: string; dataId: string; children: ReactNode }> = ({ title, dataId, children }) => {
    const classes = useStyles();

    return (
        <InsightsCard heading={title}>
            <div className={classes.notice} data-id={dataId}>{children}</div>
        </InsightsCard>
    );
};

/**
 * What the page can say, in the order the questions have to be answered: could the record be read,
 * has it been read yet, is it shared with this reader, and is there anything in it.
 *
 * One branch at a time rather than four independent conditions, because a reload that fails after an
 * earlier success leaves `loadFailed` true while `hasLoaded` stays true from the load that worked.
 * Independent conditions render the stale score underneath the notice saying there is no score,
 * which is the clean bill of health this page exists to avoid handing anybody.
 */
const StatusBody: FC<{
    model: InsightsModel;
    careMapId: string;
    clientName: string;
    firstName?: string;
}> = ({ model, careMapId, clientName, firstName }) => {
    const classes = useStyles();

    if (model.loadFailed) {
        return (
            <InsightsCard heading="Could Not Be Read">
                <InlineLoadError
                    dataId="status-load-error"
                    message="Their record could not be read just now, so there is no score to show. This is a problem reaching the record, not something about how they are doing."
                    onRetry={() => model.load()}
                />
            </InsightsCard>
        );
    }

    if (!model.hasLoaded) {
        return (
            <div className={classes.loading} data-id="status-loading">
                <IonSkeletonText animated style={{ '--border-radius': '12px', height: 260 }} />
                <IonSkeletonText animated style={{ '--border-radius': '12px', height: 140 }} />
            </div>
        );
    }

    if (!model.isShared()) {
        return (
            <StatusNotice title="Not Shared With You" dataId="status-not-shared">
                This part of their record is not shared with you, so there is nothing here for me to
                read. The rest of their care profile is on the Profile tab.
            </StatusNotice>
        );
    }

    const findings = toClientFindings({ careMapId, clientName, insights: model.getActiveAboutCareMap() });

    if (findings.length === 0) {
        // Dismissal is per reader, so an empty list can mean this reader marked everything not
        // useful. Saying "nothing has been written down yet" to them would be false, and it would
        // promise that waiting fixes it when the only thing that would is undoing their own choice.
        return model.getDismissedAboutCareMap().length > 0
            ? (
                <StatusNotice title="Nothing Left to Show" dataId="status-all-dismissed">
                    You have marked everything raised about them not useful, so there is no picture
                    for me to draw. Anything new that comes in will show up here.
                </StatusNotice>
            )
            : (
                <StatusNotice title="Nothing to Show Yet" dataId="status-empty">
                    Nothing has been raised about them yet, so there is no picture to draw. As notes
                    and recordings build up this fills in on its own.
                </StatusNotice>
            );
    }

    return <StatusCards status={buildClientStatus({ careMapId, clientName, findings, now: new Date() })} firstName={firstName} />;
};

/**
 * How one care recipient is doing, as a page rather than a dialog.
 *
 * The same diagram the agency tab shows in a modal, given room to carry what a modal could not: the
 * weeks behind the score, this week against last, and this month against the one before. A family
 * member is looking at one person rather than scanning a caseload, so the question is not "who
 * should I open" but "is this better or worse than it was", and that needs the history on screen.
 *
 * A hundred out of a hundred means nothing has been raised, which is only worth saying once
 * something has actually been read. Rendering it while insights are still loading, after a failed
 * fetch, or for a viewer whose access does not include them would hand somebody a clean bill of
 * health for a record nobody looked at, so each of those states says what it is instead.
 */
const CareStatusPageWithCaremap: FC<{ careMapId: string }> = ({ careMapId }) => {
    const classes = useStyles();

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const insightsFactory = useInstance(CaremapInsightsModelFactory);
    // The factory is what republishes the model's changes, which is how every other consumer picks
    // up insights arriving after the first render.
    useSubscription(() => insightsFactory);

    const mixpanel = useInstance(MixPanelEventEmitter);
    const tabsProvider = useInstance(CareTabsProvider);
    const nameHelper = useInstance(NameHelper);

    // The provider above renders nothing until the care map is active, so this is present in
    // practice and `forActiveCaremap()` cannot throw. The guard below is for the type: the getter is
    // declared `| undefined` whatever the provider guarantees.
    const careMap = careMapState.getActiveCaremap();
    const insightsModel = careMap ? insightsFactory.forActiveCaremap() : undefined;
    const offersStatus = Boolean(careMap && tabsProvider.offersStatus(careMap));

    useEffect(() => {
        if (offersStatus) {
            mixpanel.track('Care Status Tab Opened');
        }
    }, [mixpanel, offersStatus]);

    // The factory loads on a care map change; this covers arriving after a failed read, which would
    // otherwise leave the page on that failure for as long as the care map stays active.
    useAsyncEffect(async () => {
        if (offersStatus && insightsModel && !insightsModel.hasLoaded) {
            await insightsModel.load();
        }
    }, [insightsModel, offersStatus]);

    if (!careMap || !insightsModel) {
        return null;
    }

    // A saved link, or switching recipient while on Status, can still arrive here for a care map no
    // organization looks after.
    if (!offersStatus) {
        return <Redirect to={`/care/${careMapId}`} />;
    }

    const clientName = nameHelper.getName({
        firstName: careMap.patient?.firstName,
        lastName: careMap.patient?.lastName
    }) || 'this person';

    return (
        <InnerhivePage title="Care" tabs={tabsProvider.getTabs()}>
            <CareRecipientSwitcher />

            <div className={classes.page} data-id="care-status-page">
                <StatusBody model={insightsModel} careMapId={careMapId} clientName={clientName} firstName={careMap.patient?.firstName} />
            </div>
        </InnerhivePage>
    );
};

export const CareStatusPage: FC<{ caremapId: string }> = ({ caremapId }) => (
    <ActiveCareMapProviderV2 careMapId={caremapId}>
        <CareStatusPageWithCaremap careMapId={caremapId} />
    </ActiveCareMapProviderV2>
);
